import React, { useState, useEffect, useMemo } from "react";
import { useForm, Controller } from "react-hook-form";
import Input from "../../../components/elements/form/input";
import Icon from "../../../components/icons";
import CommonDropdown from "../../../components/elements/form/commonDropdown";
import { useLookup, getLookupDisplayValue } from "../../../_common/hookFunction";
import { LOOKUP_VALUE } from "../../../_common/constant";
import Button from "../../../components/elements/button";
import InfiniteScrollTable from "../../../components/InfiniteScrollTable";
import { useProductsApi } from "../../../store/api/hooks/productsApiHook";
import { useTranslation } from "react-i18next";

interface Props {
  onClose: () => void;
  onConfirm: (selectedProducts: any) => void;
  productDataTree?: Array<any> | null;
  category?: any;
  disableCategory?: boolean;
}

type FormValues = {
  keywordSearch: string;
  category: any;
  status: any;
};

function CustomerGroupProductSearchModal({
  onClose,
  onConfirm,
  productDataTree = [],
  category = null,
  disableCategory = false,
}: Props) {
  const { t } = useTranslation("customers");
  const lookup = useLookup();
  const productsApi = useProductsApi();

  const initialStatus = useMemo(() => {
    const statuses = lookup.getProductStatus();
    const activeStatuses = statuses
      ?.filter((s: any) => s.lookupId === LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE)
      .map((s: any) => ({ value: s.lookupId.toString(), label: getLookupDisplayValue(s, t) })) || [];
    return activeStatuses;
  }, [lookup, t]);

  const [modalProducts, setModalProducts] = useState<any[] | null>(null);
  const [modalSelectedProducts, setModalSelectedProducts] = useState<any>(productDataTree || []);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);
  const [totalRows, setTotalRows] = useState(0);
  const [searchKey, setSearchKey] = useState(0);

  const searchMethods = useForm<FormValues>({
    defaultValues: {
      keywordSearch: "",
      category: category || "",
      status: initialStatus,
    },
  });

  const { control, setValue, getValues } = searchMethods;

  const resetScrollStates = () => {
    setModalProducts(null);
    setPage(1);
    setHasMore(true);
    setTotalRows(0);
    setSearchKey((prev) => prev + 1);
  };

  const fetchProducts = (pageToFetch: number, isNewSearch = false, searchDataArg?: FormValues) => {
    const searchData = searchDataArg || getValues();
    const params: any = {
      pageNumber: pageToFetch,
      pageSize: 50,
    };
    if (searchData.keywordSearch) params.keywordSearch = searchData.keywordSearch;
    if (searchData.category?.length > 0) {
      params.categoryIds = searchData.category.map((item: any) => atob(item.value));
    } else if (searchData.category?.value) {
      params.categoryIds = [atob(searchData.category.value)];
    }
    if (searchData.status?.length > 0) params.status = searchData.status.map((item: any) => item.value);

    productsApi.searchProduct(
      params,
      (response: any) => {
        if (!response.success || !response.data) {
          setHasMore(false);
          if (isNewSearch) setModalProducts([]);
          return;
        }
        const products = response.data.items.map((item: any) => ({ ...item, id: item.productId }));
        setTotalRows(response.data.totalRows || 0);
        setHasMore(products.length > 0);
        setPage(pageToFetch);
        if (isNewSearch) {
          setModalProducts(products);
        } else {
          setModalProducts((prev) => [...(prev || []), ...products]);
        }
      },
      () => {
        setHasMore(false);
        if (isNewSearch) setModalProducts([]);
      }
    );
  };

  useEffect(() => {
    if (category) {
      setValue("category", category);
    }
    setValue("status", initialStatus);

    const initialSearchData = { keywordSearch: "", category: category || "", status: initialStatus };
    resetScrollStates();
    fetchProducts(1, true, initialSearchData);
    setModalSelectedProducts(productDataTree || []);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleSearchClick = () => {
    const currentSearchData = getValues();
    resetScrollStates();
    fetchProducts(1, true, currentSearchData);
  };

  const isProductSelected = (product: any): boolean => {
    return modalSelectedProducts?.some((p: any) => p.value === product.productId);
  };

  const isAllSelected = useMemo(() => {
    if (!modalProducts || modalProducts.length === 0) return false;
    return modalProducts.every((p: any) => isProductSelected(p));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProducts, modalSelectedProducts]);

  const isIndeterminate = useMemo(() => {
    if (!modalProducts || modalProducts.length === 0) return false;
    const selectedCount = modalProducts.filter((p: any) => isProductSelected(p)).length;
    return selectedCount > 0 && selectedCount < modalProducts.length;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [modalProducts, modalSelectedProducts]);

  const handleSelectAll = () => {
    const currentPageProducts = modalProducts || [];
    if (isAllSelected) {
      const idsOnPage = new Set(currentPageProducts.map((p: any) => p.productId));
      setModalSelectedProducts((prev: any[]) => prev.filter((p: any) => !idsOnPage.has(p.value)));
    } else {
      const newItems = currentPageProducts
        .filter((p: any) => !isProductSelected(p))
        .map((p: any) => ({ label: p.productName, value: p.productId }));
      setModalSelectedProducts((prev: any[]) => [...prev, ...newItems]);
    }
  };

  const handleProductSelection = (product: any) => {
    setModalSelectedProducts((prev: any[]) => {
      const alreadySelected = prev.some((p) => p.value === product.productId);
      if (alreadySelected) {
        return prev.filter((p) => p.value !== product.productId);
      }
      return [...prev, { label: product.productName, value: product.productId }];
    });
  };

  return (
    <div className="min-h-[70vh] max-h-[85vh] flex flex-col">
      <div className="flex-1 overflow-y-auto p-6">
        <div className="space-y-4 mb-6">
          <div className="grid grid-cols-3 gap-4">
            <div>
              <Controller
                name="keywordSearch"
                control={control}
                render={({ field }) => (
                  <Input
                    {...field}
                    label={t("productSearchModal.fields.searchProducts")}
                    type="text"
                    className="!my-0"
                    placeholder={t("productSearchModal.placeholders.searchKeyword")}
                  >
                    <span className="absolute right-2 top-1/2 transform -translate-y-1/2 pt-2">
                      <Icon name="search" className="opacity-70" />
                    </span>
                  </Input>
                )}
              />
            </div>
            <div>
              <CommonDropdown
                control={control}
                setValue={setValue}
                label={t("productSearchModal.fields.productCategory")}
                name="category"
                type="categorytreebind"
                placeholder={t("productSearchModal.placeholders.selectCategory")}
                className="!my-0 z-30"
                isMulti={true}
                disabled={disableCategory}
              />
            </div>
            <div>
              <CommonDropdown
                preSelectValues={lookup
                  .getProductStatus()
                  .filter((s: any) => s.lookupId === LOOKUP_VALUE.PRODUCT_STATUS_ACTIVE)
                  .map((s: any) => s.lookupId)}
                control={control}
                setValue={setValue}
                label={t("productSearchModal.fields.status")}
                name="status"
                type="lookup"
                placeholder={t("productSearchModal.placeholders.selectStatus")}
                className="!my-0 z-30"
                data={lookup.getProductStatus()}
                isMulti={true}
              />
            </div>
          </div>

          <div className="flex justify-end">
            <Button type="button" onClick={handleSearchClick}>
              {t("productSearchModal.buttons.search")}
            </Button>
          </div>
        </div>

        <div className="border-t pt-4">
          <div className="flex items-center justify-between mb-4">
            <h3 className="font-medium">{t("productSearchModal.values.productsFound", { count: totalRows || 0 })}</h3>
          </div>

            <InfiniteScrollTable
              key={searchKey}
              scrollId="customer-group-product-search-scroll"
              data={modalProducts}
              height={`${Math.max(400, Math.floor(window.innerHeight * 0.42))}px`}
              hasMore={hasMore && (modalProducts?.length || 0) < totalRows}
              onLoadMore={() => {
                if ((modalProducts?.length || 0) < totalRows) fetchProducts(page + 1);
              }}
              columns={[
                {
                  key: "select",
                  label: (
                    <input
                      type="checkbox"
                      checked={isAllSelected}
                      ref={(el) => {
                        if (el) el.indeterminate = isIndeterminate;
                      }}
                      onChange={handleSelectAll}
                      className="rounded h-4 w-4 cursor-pointer"
                      title={t("productSearchModal.values.selectAll")}
                    />
                  ),
                  sortable: false,
                  render: (product: any) => (
                    <input
                      type="checkbox"
                      checked={isProductSelected(product)}
                      onChange={() => handleProductSelection(product)}
                      className="rounded h-4 w-4 cursor-pointer"
                    />
                  ),
                },
                {
                  key: "productName",
                  label: t("productSearchModal.columns.productName"),
                  sortable: true,
                  render: (product: any) => (
                    <div>
                      <div className="font-medium break-words">{product.productName}</div>
                      <div className="text-sm text-gray-500">
                        {t("productSearchModal.values.skuLabel", { sku: product.autoGeneratedSKU || product.sku || t("productSearchModal.values.skuNotAvailable") })}
                      </div>
                    </div>
                  ),
                },
                {
                  key: "categoryName",
                  label: t("productSearchModal.columns.category"),
                  sortable: true,
                  render: (product: any) => <div>{product.categoryName}</div>,
                },
              ]}
            />
        </div>
      </div>
      <div className="z-50 flex justify-end gap-3 pt-4 border-t px-6 pb-6">
        <Button type="button" onClick={onClose} theme="secondary">
          {t("productSearchModal.buttons.cancel")}
        </Button>
        <Button type="button" onClick={() => onConfirm(modalSelectedProducts)}>
          {t("productSearchModal.buttons.confirmSelection")}
        </Button>
      </div>
    </div>
  );
}

export default CustomerGroupProductSearchModal;
